import React, {useEffect, useMemo, useState} from 'react';
import {createPortal} from 'react-dom';
import {ChevronLeftIcon, ChevronRightIcon} from '@heroicons/react/24/outline';
import {Card} from '../components/ui/card';
import {StatCard} from '../components/ui/StatCard';
import {Spinner} from '../components/ui/spinner';
import {Tabs} from '../components/ui/tabs';
import {FilterSelect} from '../components/ui/FilterSelect';
import {Modal} from '../components/ui/modal';
import {usePortalSlot} from '../hooks/usePortalSlot';
import {Table, TableHead, TableHeaderCell, TableBody, TableRow, TableCell} from '../components/ui/table';
import EChart from '../components/charts/EChart';
import {useChartPalette} from '../components/charts/useChartMode';
import {ChartPalette} from '../components/charts/chartTheme';
import {
    pinnedRightAxisLabel,
    rightAxisInset,
    rightAxisOffset,
    smoothLineOption,
    withAlpha,
    StepIndicatorSpec,
} from '../components/charts/options';
import {promptApi} from '../services/api/promptApi';
import {
    jobPerformanceApi,
    ReleaseMarker,
    ScheduledJobPerformance,
    ScheduledJobPerformanceByBlueprint,
    ScheduledJobPerformanceDay,
    ScheduledJobPerformanceOutcomeDay,
} from '../services/api/jobPerformanceApi';
import {
    aggregateWindow,
    clampWindowStart,
    isTodayPartial,
    latestRelease,
    latestWindowStart,
    macroAggregateWindow,
    releasesInWindow,
    splitMetricByRelease,
    versionActiveOnDate,
} from './jobPerformance/logic';

// Job Performance admin page (deepcontext-l6wj). Powered by the daily-aggregate
// endpoint (deepcontext-hpsw) and the release-markers endpoint (deepcontext-2lfx).
//
// The daily-aggregate endpoint always returns a fixed lookback window ending
// today (no offset/anchor param), so this page fetches a large buffer once and
// pages Older/Newer/Latest through it client-side (mirrors the windowing
// pattern in JobRunsModal, applied to days instead of runs).
const BUFFER_DAYS = 365;
const RANGE_OPTIONS = [7, 30, 90] as const;
type RangeDays = (typeof RANGE_OPTIONS)[number];

// Locked categorical order (deepcontext-asgs, Job Performance page lock):
// match, fetch and drop are three readings of the same "how did this job do"
// question, so all three take fixed, sequential categorical slots (s1-s3)
// rather than borrowing the reserved risk/critical status colour for drop —
// that reservation is for verdicts (safe/unsafe), not "one of three lines on
// this chart is the bad one". Matched bid requests and matched URLs are both
// volume layers drawn behind the percentages, each on its own axis, so they
// take the next two slots (s4, s5) — matched URLs is the count behind the
// match-rate line and behind matched bid requests (deepcontext-0dl7), never
// sharing a bar colour with either. Active jobs (deepcontext-b292) is the
// context that makes the volume bars interpretable — a real count, not an
// annotation — so it earns the last slot (s6) rather than sharing the
// deployment marker's recessive chrome ink. A deployment is an annotation
// rather than a series, so it stays on muted chrome ink.
function chartColors(palette: ChartPalette) {
    return {
        match: palette.categorical[0],
        fetch: palette.categorical[1],
        drop: palette.categorical[2],
        deploy: palette.chrome.inkMuted,
        bidReqs: palette.categorical[3],
        matchedUrls: palette.categorical[4],
        activeJobs: palette.categorical[5],
    };
}

// Shared alpha steps for every volume-bar layer (bid requests, matched URLs):
// a low-alpha wash so bars stay behind the lines, dimmed further on the
// still-accumulating final day.
const VOLUME_BAR_ALPHA = 0.22;
const VOLUME_BAR_ALPHA_PARTIAL = 0.11;

function pct(value: number | undefined): string {
    return `${(value ?? 0).toFixed(1)}%`;
}

function formatDelta(delta: number): string {
    const sign = delta >= 0 ? '+' : '-';
    return `${sign}${Math.abs(delta).toFixed(1)}pt`;
}

const bidReqsFormatter = new Intl.NumberFormat('en-US', {notation: 'compact', maximumFractionDigits: 1});

// Plain thousands-grouped integer for the modal's Runs/URLs columns — pinned
// to en-US for the same reason as the currency/compact formatters above.
const intFormatter = new Intl.NumberFormat('en-US');

// Range total / table cell formatting for matchedBidRequests (deepcontext-ge91.2):
// compact notation (2.1M / 890K), em-dash when there's no stored value.
function formatBidReqs(value: number | null | undefined): string {
    return value == null ? '—' : bidReqsFormatter.format(value);
}

// Matched URLs (deepcontext-0dl7) is a raw count like matchedBidRequests, so
// it reads the same way: compact notation, em-dash when there's no value.
const formatMatchedUrls = formatBidReqs;

// Spend (deepcontext-izaw.3/.4): compact for the KPI tile ($37.6K), full for
// the breakdown-modal cell ($18,420). `en-US` is pinned explicitly (rather
// than relying on the host locale) because Jest inherits the host's hr-HR
// locale, which would format the same number with a comma decimal separator.
const spendCompactFormatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    maximumFractionDigits: 1,
});
const spendFullFormatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});

/** KPI-tile spend: "—" when the DSP outcome read is unavailable, never $0. */
function formatSpendCompact(value: number | null | undefined): string {
    return value == null ? '—' : spendCompactFormatter.format(value);
}

/**
 * Blueprint-tab spend cell: blank (not "$0"/"—") when the blueprint has no
 * linked campaign — `value === null` — so an unlinked row never reads as a
 * real, zeroed-out spend figure.
 */
function formatSpendCell(value: number | null | undefined): string {
    return value == null ? '' : spendFullFormatter.format(value);
}

/** Same blank-not-zero convention as `formatSpendCell`, for the win-rate cell. */
function formatWinRateCell(value: number | null | undefined): string {
    return value == null ? '' : pct(value);
}

// A positive delta is an improvement for fetch fulfillment / match rate, but a
// regression for drop rate — `positiveIsGood` flips which direction reads green.
function deltaDirection(delta: number, positiveIsGood: boolean): 'up' | 'down' {
    const improved = positiveIsGood ? delta >= 0 : delta <= 0;
    return improved ? 'up' : 'down';
}

// Same up/down semantics as `deltaDirection`, plus a "flat" state for deltas
// that round to 0.0pt — the table renders those gray rather than colored.
function metricDeltaDirection(delta: number, positiveIsGood: boolean): 'up' | 'down' | 'flat' {
    if (Math.abs(delta).toFixed(1) === '0.0') return 'flat';
    return deltaDirection(delta, positiveIsGood);
}

type MetricDelta = {before: number; after: number; delta: number} | undefined;

// Renders the small inline delta next to a metric cell's value, per the
// design-locked mockup (jp-table .inl spans): 11px, 6px left margin, colored
// green/red/gray by direction, or an em-dash when there's no release split.
function InlineDelta({testId, delta, positiveIsGood}: {testId: string; delta: MetricDelta; positiveIsGood: boolean}) {
    if (!delta) {
        return (
            <span data-testid={testId} className="flat ml-1.5 text-sm tabular-nums text-ink-muted">
                —
            </span>
        );
    }
    const dir = metricDeltaDirection(delta.delta, positiveIsGood);
    const colorClass = dir === 'up' ? 'text-safe' : dir === 'down' ? 'text-risk' : 'text-ink-muted';
    return (
        <span data-testid={testId} className={`${dir} ml-1.5 text-sm font-medium tabular-nums ${colorClass}`}>
            {formatDelta(delta.delta)}
        </span>
    );
}

type SortKey = 'job' | 'runs' | 'fetch' | 'match' | 'drop' | 'bidReqs';
type BlueprintSortKey = 'blueprint' | 'match' | 'bidReqs' | 'winRate' | 'spend';

function SortArrow({active, dir}: {active: boolean; dir: 'asc' | 'desc'}) {
    if (!active) return <span className="ml-1 text-xs text-ink-muted">▲▼</span>;
    return <span className="ml-1 text-xs text-accent-active">{dir === 'asc' ? '▲' : '▼'}</span>;
}

function shortDate(dateStr: string): string {
    const d = new Date(`${dateStr}T00:00:00Z`);
    return d.toLocaleDateString('en-US', {month: 'short', day: 'numeric', timeZone: 'UTC'});
}

// While a tile's fetch is in flight, the value/delta area is replaced by a
// small centered spinner (deepcontext-2mb5, design option B) — the label
// stays put. Kept as the `value` StatCard receives so the spinner sits in the
// same slot the formatted value would.
function kpiValueNode(loading: boolean | undefined, value: React.ReactNode): React.ReactNode {
    return loading ? (
        <div className="flex justify-center pt-1.5">
            <Spinner size="sm" />
        </div>
    ) : (
        value
    );
}

export interface PerformanceTooltipRow {
    date: string;
    matchRatePct: number;
    fetchFulfillmentPct: number;
    dropRatePct: number;
    matchedBidRequests?: number | null;
    version?: string;
    isToday?: boolean;
}

// Values that reach the tooltip from the API (the release version) are escaped:
// ECharts renders this string as markup, so anything unescaped is injected.
function escapeHtml(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// The chart tooltip is ECharts-rendered HTML, so it is built as a string here
// (and unit-tested as one) instead of as a React node.
export function performanceTooltipHtml(row: PerformanceTooltipRow, palette: ChartPalette): string {
    const {ink, inkMuted} = palette.chrome;
    const colors = chartColors(palette);
    const row_line = (label: string, value: string, swatch: string) => `
        <div style="display:flex;align-items:center;gap:8px;justify-content:space-between">
            <span style="display:inline-flex;align-items:center;gap:6px;color:${inkMuted}">
                <span style="width:8px;height:8px;border-radius:9999px;background:${swatch}"></span>${label}
            </span>
            <strong style="color:${ink};font-variant-numeric:tabular-nums">${value}</strong>
        </div>`;
    const line = (label: string, value: number, swatch: string) => row_line(label, pct(value), swatch);

    return `
        <div style="min-width:180px;display:flex;flex-direction:column;gap:4px">
            <div style="font-weight:600;color:${ink}">${shortDate(row.date)}${
                row.isToday ? `<span style="font-weight:400;color:${inkMuted}"> (partial - still collecting)</span>` : ''
            }</div>
            ${line('Match rate', row.matchRatePct, colors.match)}
            ${line('Fetch fulfillment', row.fetchFulfillmentPct, colors.fetch)}
            ${line('Drop rate', row.dropRatePct, colors.drop)}
            ${row_line('Bid requests (matched)', formatBidReqs(row.matchedBidRequests), colors.bidReqs)}
            ${row.version ? `<div style="color:${inkMuted};font-size:11px">Version: ${escapeHtml(row.version)}</div>` : ''}
        </div>`;
}

// Raw/per-run toggle for the two volume bars (deepcontext-b292): raw daily
// sums drop whenever campaigns expire and fewer jobs run that day, which
// reads as a regression even when each job's own volume is unchanged. Per
// run divides by that day's run count so the bars compare "how much does a
// typical run pull in" instead — the default, since that's the number the
// deploy-effect story in the KPI deltas above is actually about.
export type VolumeMode = 'per-run' | 'raw';

/** `value / runs`, or null when there's nothing to divide (mirrors the null-for-no-data convention `formatBidReqs` already reads). */
function perRun(value: number | null, runs: number): number | null {
    if (value == null || runs <= 0) return null;
    return value / runs;
}

type PerformanceChartRow = ScheduledJobPerformanceDay & {
    version?: string;
    isToday: boolean;
    matchRateMain: number | null;
    fetchFulfillmentMain: number | null;
    dropRateMain: number | null;
    matchRateTail: number | null;
    fetchFulfillmentTail: number | null;
    dropRateTail: number | null;
    // The volume bars' own display values — raw sums or divided by the day's
    // run count, depending on `volumeMode`. Percentages above are untouched.
    matchedBidRequestsVolume: number | null;
    matchedUrlsVolume: number | null;
};

// The breakdown chart below stacks two right-hand volume axes (bid requests
// + matched URLs — keep this in sync with the `volumes` array in
// performanceChartOption). outcomeChartOption reserves the same right-hand
// inset even though it only has one axis of its own, so the two charts'
// plot areas share a right edge (deepcontext-m282).
const BREAKDOWN_RIGHT_AXIS_COLUMNS = 2;

// Deploy markers and the two shaded bands ride on the lead series: a dashed
// vertical markLine per release (pill-labelled with the version), a markArea
// tinting everything after the latest in-window deploy, and — when the window
// ends today — a second markArea over the still-accumulating final day.
function performanceChartOption({
    chartData,
    palette,
    partial,
    activeDeployDate,
    visibleReleases,
}: {
    chartData: PerformanceChartRow[];
    palette: ChartPalette;
    partial: boolean;
    activeDeployDate?: string;
    visibleReleases: ReleaseMarker[];
}) {
    const colors = chartColors(palette);
    const lastDate = chartData.length > 0 ? chartData[chartData.length - 1].date : undefined;

    const bands: unknown[] = [];
    if (activeDeployDate && lastDate) {
        bands.push([
            {
                name: 'after-deploy',
                xAxis: activeDeployDate,
                itemStyle: {color: withAlpha(colors.deploy, 0.07)},
                // The band is read through the deploy marker beside it; ECharts
                // would otherwise print the band's own name across the plot.
                label: {show: false},
            },
            {xAxis: lastDate},
        ]);
    }
    if (partial && chartData.length >= 2) {
        bands.push([
            {
                name: 'today-partial',
                xAxis: chartData[chartData.length - 2].date,
                itemStyle: {color: withAlpha(palette.chrome.inkMuted, 0.12)},
                label: {
                    show: true,
                    position: 'insideTop',
                    formatter: 'today - partial',
                    color: palette.chrome.inkMuted,
                    fontSize: 9,
                },
            },
            {xAxis: lastDate},
        ]);
    }

    const markLine = visibleReleases.length
        ? {
              silent: true,
              symbol: 'none',
              data: visibleReleases.map((r) => ({
                  xAxis: r.deployedAt.slice(0, 10),
                  lineStyle: {color: colors.deploy, type: [4, 3], width: 1.5},
                  label: {
                      show: true,
                      // 'end' is the top of a vertical marker; without rotate: 0
                      // ECharts turns the version pill on its side.
                      position: 'end',
                      rotate: 0,
                      formatter: r.version,
                      // Same annotation colour as the marker it labels.
                      color: colors.deploy,
                      backgroundColor: withAlpha(colors.deploy, 0.14),
                      borderRadius: 8,
                      padding: [3, 8],
                      fontSize: 10,
                      fontWeight: 600,
                  },
              })),
          }
        : undefined;

    return smoothLineOption({
        palette,
        categories: chartData.map((d) => d.date),
        axisLabelFormatter: shortDate,
        valueFormatter: (value) => `${value}%`,
        yMin: 0,
        yMax: 100,
        showLegend: false,
        tooltipFormatter: (params: unknown) => {
            // Fail closed: with no param there is no hovered day, and defaulting
            // to index 0 would quietly report the first day's numbers.
            const first = (params as Array<{dataIndex: number}> | undefined)?.[0];
            if (!first) return '';
            const row = chartData[first.dataIndex];
            return row ? performanceTooltipHtml(row, palette) : '';
        },
        volumes: [
            {
                name: 'Bid requests (matched)',
                data: chartData.map((d) => d.matchedBidRequestsVolume),
                // The still-accumulating final day reads lighter, the same way
                // its line turns into a faded dotted tail.
                colors: chartData.map((d) =>
                    withAlpha(colors.bidReqs, d.isToday ? VOLUME_BAR_ALPHA_PARTIAL : VOLUME_BAR_ALPHA),
                ),
                axisColor: colors.bidReqs,
                valueFormatter: formatBidReqs,
            },
            // Volume signal behind matchRatePct — pooled per day, not averaged
            // per blueprint (deepcontext-0dl7) — so a picker-composition shift
            // that moves the % line with no scoring change still shows up here
            // as flat/unchanged bars. Its own axis: matched URLs (~50-150k/day)
            // is a couple orders of magnitude below matched bid requests
            // (~0.5-1.2B/day) and would flatten to nothing on that scale.
            {
                name: 'Matched URLs',
                data: chartData.map((d) => d.matchedUrlsVolume),
                colors: chartData.map((d) =>
                    withAlpha(colors.matchedUrls, d.isToday ? VOLUME_BAR_ALPHA_PARTIAL : VOLUME_BAR_ALPHA),
                ),
                axisColor: colors.matchedUrls,
                valueFormatter: formatMatchedUrls,
            },
        ],
        // Thin count line, independent of the raw/per-run toggle above — how
        // many jobs actually ran that day, so a volume drop from campaign
        // churn (deepcontext-b292) reads as fewer jobs rather than a
        // deployment regression.
        stepIndicator: activeJobsStepIndicator(chartData, colors.activeJobs),
        series: [
            {
                name: 'Match rate',
                data: chartData.map((d) => d.matchRateMain),
                color: colors.match,
                area: true,
                // The lead series (s1) carries extra stroke weight so it reads
                // as the headline metric next to fetch/drop at the default width.
                lineWidth: 3,
                markArea: bands.length ? {silent: true, data: bands} : undefined,
                markLine,
            },
            {name: 'Fetch fulfillment', data: chartData.map((d) => d.fetchFulfillmentMain), color: colors.fetch},
            {name: 'Drop rate', data: chartData.map((d) => d.dropRateMain), color: colors.drop},
            ...(partial
                ? [
                      tailSeries('Match rate (partial)', chartData.map((d) => d.matchRateTail), colors.match),
                      tailSeries('Fetch fulfillment (partial)', chartData.map((d) => d.fetchFulfillmentTail), colors.fetch),
                      tailSeries('Drop rate (partial)', chartData.map((d) => d.dropRateTail), colors.drop),
                  ]
                : []),
        ],
    });
}

// The still-accumulating last day: a dotted, dimmed two-point tail with a
// hollow marker on today's point, so it never reads as a settled value.
function tailSeries(name: string, data: (number | null)[], color: string) {
    return {name, data, color, dash: [2, 5], faded: true, showSymbol: true, tooltip: false};
}

// The active-jobs indicator's own spec (deepcontext-b292) — always the raw
// day-count, never divided by the volume-bar toggle above.
function activeJobsStepIndicator(chartData: PerformanceChartRow[], color: string): StepIndicatorSpec {
    return {
        name: 'Active jobs',
        data: chartData.map((d) => d.activeJobs),
        color,
    };
}

function ChartLegend({palette, partial}: {palette: ChartPalette; partial: boolean}) {
    const colors = chartColors(palette);
    const key = (color: string, label: string, dashed = false) => (
        <span className="inline-flex items-center gap-1.5" key={label}>
            <span
                className={`inline-block w-4 border-t-2 ${dashed ? 'border-dashed' : ''}`}
                style={{borderColor: color}}
            />
            {label}
        </span>
    );
    return (
        <div className="flex flex-wrap items-center gap-4 text-xs text-ink-muted mb-2">
            {key(colors.match, 'Match rate')}
            {key(colors.fetch, 'Fetch fulfillment')}
            {key(colors.drop, 'Drop rate')}
            <span className="inline-flex items-center gap-1.5">
                <span
                    className="inline-block w-3 h-2.5 rounded-sm"
                    style={{backgroundColor: withAlpha(colors.bidReqs, 0.28)}}
                />
                Bid requests (matched)
            </span>
            <span className="inline-flex items-center gap-1.5">
                <span
                    className="inline-block w-3 h-2.5 rounded-sm"
                    style={{backgroundColor: withAlpha(colors.matchedUrls, 0.28)}}
                />
                Matched URLs
            </span>
            {key(colors.activeJobs, 'Active jobs')}
            {key(colors.deploy, 'Deployment', true)}
            {partial && key(palette.chrome.inkMuted, 'Today (partial)', true)}
        </div>
    );
}

// Second chart's own row shape: the DSP outcome day (spend/win rate), joined
// to the window's dates so the x-axis stays identical to chart one's even on
// days the DSP read has nothing for (null bar/point rather than a gap that
// shifts every later day left).
type OutcomeChartRow = {date: string; spend: number | null; winRatePct: number | null};

// Spend ($) and win rate (%) both read best at 2 decimals; null keeps the
// dash ECharts shows for a missing point.
function formatOutcomeTooltipValue(value: unknown): string {
    if (typeof value !== 'number' || !Number.isFinite(value)) return '-';
    return value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

/**
 * Campaign outcome (deepcontext-izaw.4): spend as daily bars on the left
 * axis, win rate as a line on the right axis. Matched bid requests is
 * deliberately NOT a series here — three different units (a count, a
 * currency amount and a percentage) in one chart is unreadable, so it stays
 * a KPI tile and a breakdown-modal column instead.
 */
export function outcomeChartOption({
    chartData,
    palette,
    visibleReleases,
}: {
    chartData: OutcomeChartRow[];
    palette: ChartPalette;
    visibleReleases: ReleaseMarker[];
}) {
    const colors = chartColors(palette);
    const spendColor = colors.bidReqs;
    const winRateColor = colors.match;

    const markLine = visibleReleases.length
        ? {
              silent: true,
              symbol: 'none',
              data: visibleReleases.map((r) => ({
                  xAxis: r.deployedAt.slice(0, 10),
                  lineStyle: {color: colors.deploy, type: [4, 3], width: 1.5},
                  label: {
                      show: true,
                      position: 'end',
                      rotate: 0,
                      formatter: r.version,
                      color: colors.deploy,
                      backgroundColor: withAlpha(colors.deploy, 0.14),
                      borderRadius: 8,
                      padding: [3, 8],
                      fontSize: 10,
                      fontWeight: 600,
                  },
              })),
          }
        : undefined;

    return {
        animation: false,
        grid: {left: 8, right: rightAxisInset(BREAKDOWN_RIGHT_AXIS_COLUMNS), top: 16, bottom: 4, containLabel: true},
        legend: {show: false},
        tooltip: {
            trigger: 'axis',
            confine: true,
            appendToBody: false,
            axisPointer: {type: 'shadow', shadowStyle: {color: palette.chrome.gridline, opacity: 0.6}},
            valueFormatter: formatOutcomeTooltipValue,
        },
        xAxis: {
            type: 'category',
            boundaryGap: true,
            data: chartData.map((d) => d.date),
            axisLabel: {formatter: shortDate},
        },
        yAxis: [
            {
                type: 'value',
                position: 'left',
                axisLabel: {formatter: (value: number) => formatSpendCompact(value)},
            },
            {
                type: 'value',
                position: 'right',
                min: 0,
                max: 100,
                // No volume axis of its own to sit beside — but it still
                // takes the breakdown chart's outermost column's offset, so
                // containLabel reserves the same amount of room here as it
                // does there and the two plot areas' right edges line up
                // (deepcontext-m282). The trade-off: a wider gap between
                // this axis's labels and the card edge than it would need on
                // its own, which is deliberate — see JobPerformance.tsx's
                // BREAKDOWN_RIGHT_AXIS_COLUMNS.
                offset: rightAxisOffset(BREAKDOWN_RIGHT_AXIS_COLUMNS - 1),
                splitLine: {show: false},
                axisLabel: pinnedRightAxisLabel((value) => `${value}%`, palette.chrome.inkMuted),
            },
        ],
        series: [
            {
                type: 'bar',
                name: 'Spend ($)',
                data: chartData.map((d) => d.spend),
                yAxisIndex: 0,
                itemStyle: {color: withAlpha(spendColor, 0.28), borderRadius: [4, 4, 0, 0]},
                barMaxWidth: 24,
                z: 1,
            },
            {
                type: 'line',
                name: 'Win rate (%)',
                data: chartData.map((d) => d.winRatePct),
                yAxisIndex: 1,
                smooth: true,
                smoothMonotone: 'x',
                connectNulls: false,
                showSymbol: false,
                symbolSize: 8,
                lineStyle: {width: 2, cap: 'round', join: 'round'},
                itemStyle: {color: winRateColor},
                markLine,
                z: 2,
            },
        ],
    };
}

function OutcomeChartLegend({palette}: {palette: ChartPalette}) {
    const colors = chartColors(palette);
    return (
        <div className="flex flex-wrap items-center gap-4 text-xs text-ink-muted mb-2">
            <span className="inline-flex items-center gap-1.5">
                <span
                    className="inline-block w-3 h-2.5 rounded-sm"
                    style={{backgroundColor: withAlpha(colors.bidReqs, 0.28)}}
                />
                Spend ($)
            </span>
            <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-4 border-t-2" style={{borderColor: colors.match}} />
                Win rate (%)
            </span>
        </div>
    );
}

const JobPerformance: React.FC = () => {
    const palette = useChartPalette();
    // deepcontext-6i0j.6 — the job filter / range switch / paging row used to
    // render in the page body, below AppLayout's own header row, pushing the
    // KPI strip down a line relative to Blueprints/Scheduled Jobs. It now
    // portals into the shared app-page-actions slot instead (mirrors
    // ScheduledJobsPage.tsx).
    const actionsSlot = usePortalSlot('app-page-actions');
    // Breakdown trigger (deepcontext-x68u): left-aligned, away from the
    // filters/range/pager cluster, so it portals into its own shared slot.
    const contextSlot = usePortalSlot('app-page-context');
    const [rangeDays, setRangeDays] = useState<RangeDays>(30);
    const [selectedJobId, setSelectedJobId] = useState<string>('');
    const [windowStart, setWindowStart] = useState<number | null>(null);
    const [allJobsPerf, setAllJobsPerf] = useState<ScheduledJobPerformance | null>(null);
    const [releases, setReleases] = useState<ReleaseMarker[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey | null>(null);
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
    // Chart-only, defaults to per-run (deepcontext-b292) — see VolumeMode.
    const [volumeMode, setVolumeMode] = useState<VolumeMode>('per-run');
    // Spinners (deepcontext-2mb5, design option B) show until BOTH fetches
    // below have settled — success or failure, so a fetch error clears the
    // spinner rather than leaving it spinning forever.
    const [releasesLoaded, setReleasesLoaded] = useState(false);
    const [performanceLoaded, setPerformanceLoaded] = useState(false);
    const loading = !releasesLoaded || !performanceLoaded;
    // Breakdown modal (deepcontext-izaw.4): both header buttons open the same
    // modal, each pinning it to its own tab.
    const [breakdownTab, setBreakdownTab] = useState<'job' | 'blueprint' | null>(null);
    // Blueprint id -> name, for the modal's "By blueprint" tab. Blueprints are
    // stored/fetched as "prompts" in this codebase; ids that never resolve
    // (still loading, or a stale id) fall back to the raw id, same as the
    // scheduled-jobs list does for this exact lookup.
    const [blueprintNames, setBlueprintNames] = useState<Record<string, string>>({});
    const [blueprintSortKey, setBlueprintSortKey] = useState<BlueprintSortKey | null>(null);
    const [blueprintSortDir, setBlueprintSortDir] = useState<'asc' | 'desc'>('asc');

    useEffect(() => {
        let cancelled = false;
        promptApi.getAll().then((prompts) => {
            if (cancelled) return;
            setBlueprintNames((prev) => {
                const next = {...prev};
                for (const p of prompts) next[p.id] = p.name;
                return next;
            });
        }).catch(() => {
            // Best-effort: an unresolved name just falls back to the raw id.
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        jobPerformanceApi
            .getReleases()
            .then((r) => {
                if (!cancelled) setReleases(r);
            })
            .catch(() => {
                if (!cancelled) setError('Failed to load release markers');
            })
            .finally(() => {
                if (!cancelled) setReleasesLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // includePerJobDays=1 batches every listed job's own daily series into
    // this one response (deepcontext-qwjt), so the page no longer fans out a
    // separate request per job just to prefetch its breakdown for the
    // dropdown/table.
    useEffect(() => {
        let cancelled = false;
        jobPerformanceApi
            .getPerformance(BUFFER_DAYS, undefined, true)
            .then((perf) => {
                if (!cancelled) setAllJobsPerf(perf);
            })
            .catch(() => {
                if (!cancelled) setError('Failed to load job performance');
            })
            .finally(() => {
                if (!cancelled) setPerformanceLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Re-pin to the latest window whenever the visible range or job filter changes.
    useEffect(() => {
        setWindowStart(null);
    }, [rangeDays, selectedJobId]);

    // The selected job's own daily series comes straight out of the batched
    // payload — selection never triggers a fetch.
    const selectedDays = selectedJobId ? allJobsPerf?.perJobDays?.[selectedJobId] ?? null : allJobsPerf?.days ?? null;
    const total = selectedDays?.length ?? 0;

    const win = useMemo(() => {
        const desired = windowStart === null ? latestWindowStart(rangeDays, total) : windowStart;
        const start = clampWindowStart(desired, rangeDays, total);
        return {start, end: Math.min(total, start + rangeDays)};
    }, [windowStart, rangeDays, total]);

    const visibleDays = useMemo(() => selectedDays?.slice(win.start, win.end) ?? [], [selectedDays, win]);
    const visibleReleases = useMemo(() => releasesInWindow(releases, visibleDays), [releases, visibleDays]);
    const activeDeploy = latestRelease(visibleReleases);
    const activeDeployDate = activeDeploy?.deployedAt.slice(0, 10);

    // Every listed job's own slice of the visible window — the basis for both
    // the all-jobs headline percentages (per-blueprint means) and their deltas,
    // which must compare the SAME jobs on both sides of the deploy so a job
    // that only ran before/after it can't masquerade as a deploy effect.
    // `null` until the batched payload itself has loaded, so the KPI strip
    // doesn't render numbers that then shift once the fetch settles.
    const perJobVisibleDaySets = useMemo(() => {
        if (!allJobsPerf) return null;
        const sets: ScheduledJobPerformanceDay[][] = [];
        for (const job of allJobsPerf.byJob) {
            const jobDays = allJobsPerf.perJobDays?.[job.jobId];
            if (!jobDays) return null;
            sets.push(jobDays.slice(win.start, win.end));
        }
        return sets;
    }, [allJobsPerf, win]);

    const atOldest = win.start <= 0;
    const atLatest = win.end >= total;

    const moveWindow = (deltaWindows: number) => {
        setWindowStart(clampWindowStart(win.start + deltaWindows * rangeDays, rangeDays, total));
    };

    // The final day's numbers are still accumulating when the visible window
    // ends today — split each series into a solid "main" line (stops the day
    // before) and a faded dotted "tail" (yesterday -> today) so the chart
    // reads as in-progress rather than a finished data point.
    const todayIso = new Date().toISOString().slice(0, 10);
    const partial = isTodayPartial(visibleDays, atLatest, todayIso);
    const chartData = useMemo(
        () =>
            visibleDays.map((d, i) => {
                const isToday = partial && i === visibleDays.length - 1;
                const isYesterday = partial && i === visibleDays.length - 2;
                return {
                    ...d,
                    version: versionActiveOnDate(releases, d.date),
                    isToday,
                    matchRateMain: isToday ? null : d.matchRatePct,
                    fetchFulfillmentMain: isToday ? null : d.fetchFulfillmentPct,
                    dropRateMain: isToday ? null : d.dropRatePct,
                    matchRateTail: isToday || isYesterday ? d.matchRatePct : null,
                    fetchFulfillmentTail: isToday || isYesterday ? d.fetchFulfillmentPct : null,
                    dropRateTail: isToday || isYesterday ? d.dropRatePct : null,
                    matchedBidRequestsVolume:
                        volumeMode === 'per-run' ? perRun(d.matchedBidRequests, d.runs) : d.matchedBidRequests ?? null,
                    matchedUrlsVolume: volumeMode === 'per-run' ? perRun(d.matchedUrls, d.runs) : d.matchedUrls ?? null,
                };
            }),
        [visibleDays, releases, partial, volumeMode],
    );

    const chartOption = useMemo(
        () => performanceChartOption({chartData, palette, partial, activeDeployDate, visibleReleases}),
        [chartData, palette, partial, activeDeployDate, visibleReleases],
    );

    // Second chart's data: the DSP outcome's own daily series, joined onto the
    // SAME dates as chart one (it's computed over the identical dateList on
    // the backend) so the two charts' x-axes always line up.
    const outcome = allJobsPerf?.outcome;
    const outcomeByDate = useMemo(() => {
        const map = new Map<string, ScheduledJobPerformanceOutcomeDay>();
        for (const d of outcome?.daily ?? []) map.set(d.date, d);
        return map;
    }, [outcome]);
    const outcomeChartData = useMemo(
        () =>
            chartData.map((d) => {
                const o = outcomeByDate.get(d.date);
                return {date: d.date, spend: o?.spend ?? null, winRatePct: o?.winRatePct ?? null};
            }),
        [chartData, outcomeByDate],
    );
    const outcomeOption = useMemo(
        () => outcomeChartOption({chartData: outcomeChartData, palette, visibleReleases}),
        [outcomeChartData, palette, visibleReleases],
    );

    // The backend's own `totals` aggregates whatever `days` was requested
    // (the full BUFFER_DAYS fetch) — the KPI strip and title-row meta need the
    // VISIBLE window's totals instead, so they're recomputed client-side.
    // Counts (runs / completed / URLs) always come from the day rows, which
    // already sum every job that ran; only the percentages differ by view.
    const totals = aggregateWindow(visibleDays);
    // All jobs: each blueprint's own window percentage, averaged with equal
    // weight (deepcontext-s46g), so one high-volume job can't set the headline.
    // Undefined until every job's breakdown has loaded -> "—" placeholder,
    // matching how the headline deltas behave while loading.
    const macroTotals = selectedJobId ? undefined : macroAggregateWindow(perJobVisibleDaySets);
    const headlinePct = (metricKey: 'fetchFulfillmentPct' | 'matchRatePct' | 'dropRatePct') => {
        if (selectedJobId) return pct(totals[metricKey]);
        return macroTotals ? pct(macroTotals[metricKey]) : '—';
    };
    const selectedJobName = selectedJobId ? allJobsPerf?.byJob.find((j) => j.jobId === selectedJobId)?.jobName : undefined;

    // One row per job with its window aggregates and per-metric release-split
    // deltas, computed once so both rendering and sorting read the same data.
    const tableRows = useMemo(() => {
        return (allJobsPerf?.byJob ?? [])
            .map((job) => {
                const jobDays = allJobsPerf?.perJobDays?.[job.jobId];
                // No daily series for this job in the batched payload — skip
                // rather than rendering it as a (misleading) 0-run row.
                if (jobDays === undefined) return null;
                const jobVisibleDays = jobDays.slice(win.start, win.end);
                const jobWindowTotals = aggregateWindow(jobVisibleDays);
                // No runs within the visible window — exclude, mirroring the
                // backend's exclude-0-run-jobs filter but scoped to this window.
                if (jobWindowTotals.runs === 0) return null;
                return {
                    jobId: job.jobId,
                    jobName: job.jobName,
                    runs: jobWindowTotals.runs,
                    urlsProcessed: jobWindowTotals.urlsProcessed,
                    fetch: jobWindowTotals.fetchFulfillmentPct,
                    match: jobWindowTotals.matchRatePct,
                    drop: jobWindowTotals.dropRatePct,
                    bidReqs: jobWindowTotals.matchedBidRequests,
                    fetchDelta: splitMetricByRelease(jobVisibleDays, activeDeployDate, 'fetchFulfillmentPct'),
                    matchDelta: splitMetricByRelease(jobVisibleDays, activeDeployDate, 'matchRatePct'),
                    dropDelta: splitMetricByRelease(jobVisibleDays, activeDeployDate, 'dropRatePct'),
                };
            })
            .filter((row): row is NonNullable<typeof row> => row !== null);
    }, [allJobsPerf, win, activeDeployDate]);

    // No sort key selected yet -> keep the original (byJob) order untouched.
    const sortedRows = useMemo(() => {
        if (!sortKey) return tableRows;
        const dir = sortDir === 'asc' ? 1 : -1;
        return [...tableRows].sort((a, b) => {
            if (sortKey === 'job') return a.jobName.localeCompare(b.jobName) * dir;
            // matchedBidRequests can be null (no stored data) — treat as the lowest value either way.
            if (sortKey === 'bidReqs') return ((a.bidReqs ?? -1) - (b.bidReqs ?? -1)) * dir;
            return (a[sortKey] - b[sortKey]) * dir;
        });
    }, [tableRows, sortKey, sortDir]);

    // Blueprint-tab rows: straight off `byBlueprint`, in the order the backend
    // returned them (no sort control on this tab). Names resolve through the
    // prompt-id map fetched above; an id that hasn't resolved yet falls back
    // to the raw id rather than blocking the row.
    const blueprintRows: (ScheduledJobPerformanceByBlueprint & {blueprintName: string})[] = useMemo(
        () =>
            (allJobsPerf?.byBlueprint ?? []).map((b) => ({
                ...b,
                blueprintName: blueprintNames[b.blueprintId] ?? b.blueprintId,
            })),
        [allJobsPerf, blueprintNames],
    );

    // Blueprint-tab sort: same click-to-sort/toggle-direction behavior as the
    // job tab, kept on its own state so switching tabs never carries a sort
    // selection over onto the other tab's (differently-shaped) rows.
    const sortedBlueprintRows = useMemo(() => {
        if (!blueprintSortKey) return blueprintRows;
        const dir = blueprintSortDir === 'asc' ? 1 : -1;
        return [...blueprintRows].sort((a, b) => {
            if (blueprintSortKey === 'blueprint') return a.blueprintName.localeCompare(b.blueprintName) * dir;
            if (blueprintSortKey === 'match') return (a.matchRatePct - b.matchRatePct) * dir;
            if (blueprintSortKey === 'bidReqs') return ((a.matchedBidRequests ?? -1) - (b.matchedBidRequests ?? -1)) * dir;
            if (blueprintSortKey === 'winRate') return ((a.winRatePct ?? -1) - (b.winRatePct ?? -1)) * dir;
            return ((a.spend ?? -1) - (b.spend ?? -1)) * dir;
        });
    }, [blueprintRows, blueprintSortKey, blueprintSortDir]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(key);
            setSortDir('asc');
        }
    };

    const handleBlueprintSort = (key: BlueprintSortKey) => {
        if (blueprintSortKey === key) {
            setBlueprintSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setBlueprintSortKey(key);
            setBlueprintSortDir('asc');
        }
    };

    // Alignment/interaction classes only — TableHeaderCell already bakes in
    // the shared shell's own text-ink-muted, so the active-sort color below
    // is applied on an inner <span> rather than stacked on the same element
    // (stacking two text-color utilities on one node would leave the winner
    // decided by Tailwind's class-generation order rather than by anything
    // asserted in code).
    // `sticky top-0` keeps the column row visible while rows scroll inside
    // the dialog's own scroll container — bg-surface stops rows sliding
    // underneath it from showing through.
    const headerClass = (align: 'left' | 'right' = 'right') =>
        `${align === 'left' ? 'text-left' : 'text-right'} cursor-pointer select-none whitespace-nowrap sticky top-0 z-10 bg-surface`;

    // The active sort column's header renders in accent-ink (per the page
    // lock); every other header — including Bid reqs — stays muted until it's
    // the one being sorted on. The Bid reqs *value* cells stay accent-ink
    // always (that column's own hero styling), which is unrelated to sorting.
    const headerLabelClass = (key: SortKey) => (sortKey === key ? 'text-accent-active' : undefined);
    const blueprintHeaderLabelClass = (key: BlueprintSortKey) => (blueprintSortKey === key ? 'text-accent-active' : undefined);

    return (
        <div data-testid="job-performance-root" className="w-full space-y-3">
            {/* Actions row (deepcontext-asgs page lock, hoisted into the
                shared slot by deepcontext-6i0j.6): muted context line, job
                pill select, 7d/30d/90d segmented switch, and the
                Older/Newer/Latest pill nav all share AppLayout's header row
                — mirrors ScheduledJobsPage.tsx, so the KPI strip below
                starts at the same top line as Blueprints/Scheduled Jobs. No
                in-page title — the sidebar nav item already says "Job
                Performance". */}
            {/* Breakdown trigger (deepcontext-x68u): a single pill, far left,
                separate from the filters/range/pager cluster on the right. */}
            {contextSlot &&
                createPortal(
                    <button
                        type="button"
                        onClick={() => setBreakdownTab('job')}
                        className="inline-flex h-[42px] items-center px-3 text-xs font-bold rounded-full border border-accent text-accent-active hover:bg-nav-active"
                    >
                        Breakdown
                    </button>,
                    contextSlot,
                )}

            {actionsSlot &&
                createPortal(
                    <>
                        <span className="text-sm text-ink-muted whitespace-nowrap">
                            {selectedJobName ?? 'All active jobs'} · <span className="tabular-nums">{totals.runs.toLocaleString()} runs</span>
                            {visibleDays.length > 0 && (
                                <>
                                    {' '}
                                    ·{' '}
                                    <span className="tabular-nums">
                                        {shortDate(visibleDays[0].date)} – {shortDate(visibleDays[visibleDays.length - 1].date)}
                                    </span>
                                </>
                            )}
                        </span>

                        <FilterSelect
                            data-testid="job-filter"
                            value={selectedJobId}
                            onChange={setSelectedJobId}
                            searchable
                            options={[
                                {value: '', label: 'All jobs'},
                                ...(allJobsPerf?.byJob ?? []).map((job) => ({value: job.jobId, label: job.jobName})),
                            ]}
                        />

                        <Tabs
                            aria-label="Date range"
                            tabs={RANGE_OPTIONS.map((r) => ({value: String(r), label: `${r}d`}))}
                            value={String(rangeDays)}
                            onChange={(v) => setRangeDays(Number(v) as RangeDays)}
                        />

                        <div className="pnav flex items-center gap-1.5">
                            <button
                                type="button"
                                onClick={() => moveWindow(-1)}
                                disabled={atOldest}
                                className="inline-flex h-[42px] items-center gap-1 px-3 text-xs font-bold rounded-full border border-line text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <ChevronLeftIcon className="h-3.5 w-3.5" />
                                Older
                            </button>
                            <button
                                type="button"
                                onClick={() => moveWindow(1)}
                                disabled={atLatest}
                                className="inline-flex h-[42px] items-center gap-1 px-3 text-xs font-bold rounded-full border border-line text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Newer
                                <ChevronRightIcon className="h-3.5 w-3.5" />
                            </button>
                            <button
                                type="button"
                                onClick={() => setWindowStart(null)}
                                disabled={atLatest}
                                className="inline-flex h-[42px] items-center px-3 text-xs font-bold rounded-full border border-line text-ink hover:bg-canvas disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Latest
                            </button>
                        </div>
                    </>,
                    actionsSlot,
                )}

            {error && <div className="text-sm text-risk">{error}</div>}

            {/* KPI strip is the first "row" of the page proper (deepcontext-jlkv
                alignment), laid directly on the canvas rather than nested
                inside a wrapper card. Six tiles, quality trio then outcome
                trio (deepcontext-izaw.4 page lock) — Runs and URLs processed
                moved into the breakdown modal's job tab, and the delta lines
                the quality trio used to carry moved with the per-job/
                per-blueprint rows into that same modal, since the locked
                mockup's tiles carry a value only. */}
            <div data-testid="kpi-strip" className="grid grid-cols-2 md:grid-cols-6 gap-3">
                <StatCard
                    testId="kpi-fetch-fulfillment"
                    label="Fetch fulfillment"
                    value={kpiValueNode(loading, headlinePct('fetchFulfillmentPct'))}
                />
                <StatCard
                    testId="kpi-match-rate"
                    label="Match rate"
                    value={kpiValueNode(loading, headlinePct('matchRatePct'))}
                />
                <StatCard
                    testId="kpi-drop-rate"
                    label="Drop rate"
                    value={kpiValueNode(loading, headlinePct('dropRatePct'))}
                />
                <StatCard
                    testId="kpi-bid-reqs"
                    label="Matched bid requests"
                    value={kpiValueNode(loading, formatBidReqs(totals.matchedBidRequests))}
                />
                <StatCard
                    testId="kpi-win-rate"
                    label="Win rate"
                    value={kpiValueNode(loading, outcome && outcome.available ? pct(outcome.totals.winRatePct) : '—')}
                />
                <StatCard
                    testId="kpi-spend"
                    label="Spend"
                    value={kpiValueNode(loading, outcome && outcome.available ? formatSpendCompact(outcome.totals.spend) : '—')}
                />
            </div>

            {/* Chart: its own 16px card, separate from the table below
                (deepcontext-asgs / deepcontext-it9d — sections lay out as
                distinct cards rather than one page-wide wrapper). */}
            <Card>
                <div className="p-4">
                    <div className="flex items-center justify-between gap-3">
                        <h4 className="text-sm font-medium text-ink">Daily per-blueprint averages across scheduled jobs</h4>
                        {/* Raw/per-run toggle for the volume bars only (deepcontext-b292)
                            — defaults to per-run so a campaign-expiry job-count drop
                            doesn't read as a deployment regression; the percentage
                            lines above are unaffected either way. */}
                        <div role="group" aria-label="Volume basis" className="seg inline-flex rounded-full bg-ghost-bg p-0.5">
                            {(
                                [
                                    ['per-run', 'Per run'],
                                    ['raw', 'Raw'],
                                ] as const
                            ).map(([mode, label]) => (
                                <button
                                    key={mode}
                                    type="button"
                                    data-testid={`volume-mode-${mode}`}
                                    onClick={() => setVolumeMode(mode)}
                                    className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${
                                        mode === volumeMode
                                            ? 'seg-on bg-surface text-accent-active shadow-sm'
                                            : 'text-ink-muted hover:text-ink'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="mb-2 mt-0.5 text-xs text-ink-muted tabular-nums">
                        {visibleDays.length > 0 &&
                            `${shortDate(visibleDays[0].date)} – ${shortDate(visibleDays[visibleDays.length - 1].date)} · `}
                        deltas compare before/after latest deployment (jobs active on both sides)
                    </div>

                    <ChartLegend palette={palette} partial={partial} />

                    <div className="relative rounded-xl bg-canvas p-3">
                        <EChart
                            option={chartOption}
                            height={296}
                            ariaLabel="Daily fetch, match and drop rates with matched bid requests"
                        />
                        {loading && (
                            <div
                                data-testid="job-performance-chart-spinner"
                                className="absolute inset-0 flex items-center justify-center bg-canvas"
                            >
                                <Spinner />
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* Second chart (deepcontext-izaw.4): spend/win-rate, its own
                card. When the DSP outcome read is unavailable, this card
                shows an inline message instead of the chart — the rest of
                the page (tiles, chart one) renders normally either way. */}
            <Card>
                <div className="p-4">
                    <div className="flex items-center justify-between gap-3">
                        <h4 className="text-sm font-medium text-ink">Campaign outcome</h4>
                    </div>
                    <div className="mb-2 mt-0.5 text-xs text-ink-muted">
                        spend (bars, left axis) vs. win rate (line, right axis), daily
                    </div>

                    {!loading && outcome && !outcome.available ? (
                        <div
                            data-testid="outcome-chart-unavailable"
                            className="flex items-center justify-center rounded-xl bg-canvas p-3 text-sm text-ink-muted"
                            style={{height: 296}}
                        >
                            Campaign outcome data is unavailable right now.
                        </div>
                    ) : (
                        <>
                            <OutcomeChartLegend palette={palette} />
                            <div className="relative rounded-xl bg-canvas p-3">
                                <EChart option={outcomeOption} height={296} ariaLabel="Daily spend and win rate" />
                                {loading && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-canvas">
                                        <Spinner />
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </Card>

            {/* Breakdown modal (deepcontext-izaw.4 page lock): both header
                buttons open this same modal, each pinning it to its own tab.
                The page itself keeps only the two charts above — neither
                per-job nor per-blueprint breakdown renders outside this
                modal. No column totals on either tab: a campaign can be
                shared between blueprints, so a spend/win-rate total would
                overstate. */}
            <Modal
                open={breakdownTab !== null}
                onClose={() => setBreakdownTab(null)}
                // The shell's own base class hard-coded max-w-md, which won
                // over any max-w-* passed through className regardless of
                // Tailwind's generation order — maxWidthClassName replaces
                // that slot outright instead of competing with it.
                maxWidthClassName="max-w-5xl"
                className="max-h-[80vh] flex flex-col"
            >
                {breakdownTab !== null && (
                    <div data-testid="breakdown-modal" className="flex flex-col min-h-0">
                        <div className="mb-4 flex items-center gap-3 flex-shrink-0">
                            <h2 className="text-lg font-semibold text-ink">Breakdown</h2>
                            <Tabs
                                aria-label="Breakdown"
                                tabs={[
                                    {value: 'job', label: 'By job'},
                                    {value: 'blueprint', label: 'By blueprint'},
                                ]}
                                value={breakdownTab}
                                onChange={(v) => setBreakdownTab(v as 'job' | 'blueprint')}
                            />
                            <span className="grow" />
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setBreakdownTab(null)}
                                className="text-ink-muted hover:text-ink"
                            >
                                ✕
                            </button>
                        </div>

                        {breakdownTab === 'job' ? (
                            <div className="overflow-auto min-h-0 rounded-2xl border border-line bg-surface shadow-sm">
                                <Table>
                                    <TableHead>
                                        <tr>
                                            <TableHeaderCell onClick={() => handleSort('job')} className={headerClass('left')}>
                                                <span className={headerLabelClass('job')}>Job</span>
                                                <SortArrow active={sortKey === 'job'} dir={sortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleSort('runs')} className={headerClass()}>
                                                <span className={headerLabelClass('runs')}>Runs</span>
                                                <SortArrow active={sortKey === 'runs'} dir={sortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell className={headerClass()}>URLs</TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleSort('fetch')} className={headerClass()}>
                                                <span className={headerLabelClass('fetch')}>Fetch %</span>
                                                <SortArrow active={sortKey === 'fetch'} dir={sortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleSort('match')} className={headerClass()}>
                                                <span className={headerLabelClass('match')}>Match %</span>
                                                <SortArrow active={sortKey === 'match'} dir={sortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleSort('drop')} className={headerClass()}>
                                                <span className={headerLabelClass('drop')}>Drop %</span>
                                                <SortArrow active={sortKey === 'drop'} dir={sortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleSort('bidReqs')} className={headerClass()}>
                                                <span className={headerLabelClass('bidReqs')}>Bid reqs</span>
                                                <SortArrow active={sortKey === 'bidReqs'} dir={sortDir} />
                                            </TableHeaderCell>
                                        </tr>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={7} className="py-6">
                                                    <div data-testid="job-performance-table-spinner" className="flex justify-center">
                                                        <Spinner />
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedRows.map((row) => (
                                                <TableRow key={row.jobId}>
                                                    <TableCell
                                                        data-testid={`job-name-${row.jobId}`}
                                                        className="font-medium max-w-[22rem] truncate"
                                                        title={row.jobName}
                                                    >
                                                        {row.jobName}
                                                    </TableCell>
                                                    <TableCell data-testid={`job-runs-${row.jobId}`} className="text-right tabular-nums">
                                                        {intFormatter.format(row.runs)}
                                                    </TableCell>
                                                    <TableCell data-testid={`job-urls-${row.jobId}`} className="text-right tabular-nums">
                                                        {intFormatter.format(row.urlsProcessed)}
                                                    </TableCell>
                                                    <TableCell data-testid={`job-fetch-${row.jobId}`} className="text-right tabular-nums whitespace-nowrap">
                                                        {pct(row.fetch)}
                                                        <InlineDelta testId={`job-fetch-delta-${row.jobId}`} delta={row.fetchDelta} positiveIsGood />
                                                    </TableCell>
                                                    <TableCell data-testid={`job-match-${row.jobId}`} className="text-right tabular-nums whitespace-nowrap">
                                                        {pct(row.match)}
                                                        <InlineDelta testId={`job-match-delta-${row.jobId}`} delta={row.matchDelta} positiveIsGood />
                                                    </TableCell>
                                                    <TableCell data-testid={`job-drop-${row.jobId}`} className="text-right tabular-nums whitespace-nowrap">
                                                        {pct(row.drop)}
                                                        <InlineDelta testId={`job-drop-delta-${row.jobId}`} delta={row.dropDelta} positiveIsGood={false} />
                                                    </TableCell>
                                                    <TableCell data-testid={`job-bidreqs-${row.jobId}`} className="text-right tabular-nums font-bold whitespace-nowrap">
                                                        <span className="text-accent-active">{formatBidReqs(row.bidReqs)}</span>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="overflow-auto min-h-0 rounded-2xl border border-line bg-surface shadow-sm">
                                <Table>
                                    <TableHead>
                                        <tr>
                                            <TableHeaderCell onClick={() => handleBlueprintSort('blueprint')} className={headerClass('left')}>
                                                <span className={blueprintHeaderLabelClass('blueprint')}>Blueprint</span>
                                                <SortArrow active={blueprintSortKey === 'blueprint'} dir={blueprintSortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleBlueprintSort('match')} className={headerClass()}>
                                                <span className={blueprintHeaderLabelClass('match')}>Match %</span>
                                                <SortArrow active={blueprintSortKey === 'match'} dir={blueprintSortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleBlueprintSort('bidReqs')} className={headerClass()}>
                                                <span className={blueprintHeaderLabelClass('bidReqs')}>Bid reqs</span>
                                                <SortArrow active={blueprintSortKey === 'bidReqs'} dir={blueprintSortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleBlueprintSort('winRate')} className={headerClass()}>
                                                <span className={blueprintHeaderLabelClass('winRate')}>Win rate</span>
                                                <SortArrow active={blueprintSortKey === 'winRate'} dir={blueprintSortDir} />
                                            </TableHeaderCell>
                                            <TableHeaderCell onClick={() => handleBlueprintSort('spend')} className={headerClass()}>
                                                <span className={blueprintHeaderLabelClass('spend')}>Spend</span>
                                                <SortArrow active={blueprintSortKey === 'spend'} dir={blueprintSortDir} />
                                            </TableHeaderCell>
                                        </tr>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan={5} className="py-6">
                                                    <div className="flex justify-center">
                                                        <Spinner />
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            sortedBlueprintRows.map((row) => (
                                                <TableRow key={row.blueprintId}>
                                                    <TableCell
                                                        data-testid={`blueprint-name-${row.blueprintId}`}
                                                        className="font-medium max-w-[22rem] truncate"
                                                        title={row.blueprintName}
                                                    >
                                                        {row.blueprintName}
                                                    </TableCell>
                                                    <TableCell data-testid={`blueprint-match-${row.blueprintId}`} className="text-right tabular-nums whitespace-nowrap">
                                                        {pct(row.matchRatePct)}
                                                        <InlineDelta testId={`blueprint-match-delta-${row.blueprintId}`} delta={undefined} positiveIsGood />
                                                    </TableCell>
                                                    <TableCell data-testid={`blueprint-bidreqs-${row.blueprintId}`} className="text-right tabular-nums font-bold whitespace-nowrap">
                                                        <span className="text-accent-active">{formatBidReqs(row.matchedBidRequests)}</span>
                                                        <InlineDelta testId={`blueprint-bidreqs-delta-${row.blueprintId}`} delta={undefined} positiveIsGood />
                                                    </TableCell>
                                                    <TableCell data-testid={`blueprint-winrate-${row.blueprintId}`} className="text-right tabular-nums whitespace-nowrap">
                                                        {formatWinRateCell(row.winRatePct)}
                                                    </TableCell>
                                                    <TableCell data-testid={`blueprint-spend-${row.blueprintId}`} className="text-right tabular-nums whitespace-nowrap">
                                                        {formatSpendCell(row.spend)}
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default JobPerformance;
